// BackendTraceReviewPanel -- the verdict ledger for one open trace, read from
// the saved backend review.
//
// The trace-review pipeline judges each conversation window as it grows and
// saves one report per window (traceReviewReports.js). Its normalizer is this
// ledger's rule set run server-side: every finding arrives rated on the two
// fixed axes (behaviour risk, content sensitivity), weighed against the
// tenant's policy, norms and intents where the review profile carries them,
// and admitted as actionable or withheld. The panel renders the saved reports
// at three altitudes of the same judgment: the matrix rollup, a turn timeline
// (where the evidence sits, and which turns the reports covered), and the
// ledger of actionable findings with per-frame verdict dots. Opening it reads
// what the backend produced; it never starts a review.
//
// Located citations are also handed up to ContextPanel via onEvidence as
// signal-highlight entries (block locs), so the evidence gets the same
// in-text marks + locator-pill navigation fired signals get -- one highlight
// surface, not a second one.
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    buildReportsTimeline,
    fetchTraceReviewReports,
    readReportsCache,
    runPooled,
    writeReportsCache,
} from "../lib/traceReviewReports.js";
import { getReviewAvailability, getReviewProfile } from "../lib/reviewClient.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";
import { behaviourTone, contentTone, incidentTone, SEVERITY_COLOR } from "../lib/traceReview.js";
import {
    assertedRungs,
    BEHAVIOUR_SEVERITIES,
    CONTENT_SENSITIVITIES,
    incidentReason,
    parseConstitution,
    turnText,
} from "../lib/traceEvaluation.js";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { adaptBackendFindingToLanes } from "./TraceReviewFindingCard.jsx";
import { recordTurnBase } from "../lib/traceTurnFocus.js";

// The bordered-card idiom of the surrounding trace-detail sections.
const CARD_CLASS =
    "rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[6px] min-w-0";
const LINK_CLASS =
    "shrink-0 cursor-pointer underline decoration-dotted underline-offset-2 text-[10px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]";

const PANEL_CACHE = new Map();
const PANEL_CACHE_MAX = 32;
const PROFILE_SCOPE_LIMIT = 16;
const PROFILE_FETCH_CONCURRENCY = 4;
// Window summaries shown before the rest fold behind a disclosure: most
// conversations have one report, and a long-lived one can carry hundreds.
const SUMMARY_PREVIEW = 3;
onAuthIdentityChange(() => PANEL_CACHE.clear());

function panelCacheKey(traceId) {
    return `${getAuthIdentityEpoch()}:${traceId}`;
}

function readPanelCache(traceId) {
    return PANEL_CACHE.get(panelCacheKey(traceId)) || null;
}

function writePanelCache(traceId, state) {
    if (PANEL_CACHE.size >= PANEL_CACHE_MAX) PANEL_CACHE.delete(PANEL_CACHE.keys().next().value);
    PANEL_CACHE.set(panelCacheKey(traceId), state);
}

export function clearBackendTraceReviewCache() {
    PANEL_CACHE.clear();
}

// -- saved reports -> the ledger ------------------------------------------------

// The pipeline's frame verdicts in the ledger's vocabulary. The normalizer
// names the agreeing verdicts differently, and incidentReason's cross-frame
// signatures (a written clause routinely broken, a risk no clause covers)
// read the ledger's names.
const LEDGER_STATUS = {
    policy: { consistent: "cleared" },
    norms: { consistent: "typical" },
    intent: { aligned: "advances" },
};

// Why the pipeline admitted a finding, as the ledger's mismatch kind.
const MISMATCH_OF_REASON = {
    risk: "risk",
    sensitivity: "sensitivity",
    policy: "policy",
    off_mission: "off-mission",
    norm_deviation: "norm-deviation",
    injection: "injection",
};

const inLedgerVocabulary = (kind, frame) => (frame
    ? { ...frame, status: LEDGER_STATUS[kind][frame.status] || frame.status }
    : null);

const scopeKeyOf = (report) => (report?.scope?.key ? `${report.scope.kind}:${report.scope.key}` : "tenant");

// The ledger lists what a reader would act on. A finding without a reason was
// described by the judge and admitted by no gate; the pipeline keeps it in the
// document and out of every count, and so does the ledger.
export function isActionableFinding(finding) {
    return !!finding?.actionable || !!finding?.reason;
}

// Findings the judge described that nothing objected to. Context-only and
// unsupported findings are not counted: their evidence never reached the
// reviewed window.
function isWithheldFinding(finding) {
    const evidence = finding?.evidenceStatus;
    return !isActionableFinding(finding) && evidence !== "context_only" && evidence !== "unsupported";
}

export function ledgerIncident(finding, turns = []) {
    const inc = adaptBackendFindingToLanes(finding, turns);
    return {
        ...inc,
        policy: inLedgerVocabulary("policy", inc.policy),
        norms: inLedgerVocabulary("norms", inc.norms),
        intent: inLedgerVocabulary("intent", inc.intent),
        mismatch: MISMATCH_OF_REASON[inc.reason] || null,
        partial: inc.evidenceStatus === "partial",
        // The pipeline verified the evidence against the whole record before
        // admitting the finding, so a quote the loaded turns do not carry is a
        // question of which page is loaded, not of support.
        unplaced: inc.uncited,
        earlier: inc.uncited && inc.citations.some((c) => c.unloaded),
        uncited: false,
    };
}

// The review profile's three documents in the shapes the ledger rows resolve
// references against.
export function ledgerDocuments(profile) {
    const item = (entry) => ({
        ref: entry.reference,
        title: entry.title || "",
        description: entry.description || "",
        basis: entry.basis || "",
    });
    return {
        clauses: parseConstitution(profile?.policyText || ""),
        norms: (profile?.norms || []).map((n) => ({ ...item(n), prevalence: n.prevalence || "" })),
        intents: (profile?.intents || []).map((n) => ({ ...item(n), weight: n.weight || "" })),
        summary: profile?.discoverySummary || "",
    };
}

const findingKey = (finding) => {
    const cited = (finding.citations || [])
        .map((citation) => `${citation.source?.messageId || ""}|${citation.quote || ""}`)
        .sort()
        .join(";;");
    return `${String(finding.title || "").toLowerCase()}::${cited}`;
};

// The judge names its window's items by ref -- t1, t2, ... for the targets and
// c1, c2, ... for the context before them, each counted from one in the order
// the report lists them -- and its summary and note cite turns that way. Each
// ref prints as the number the timeline and Lanes give that item's turn, which
// the window's own count is not. `l` refs name lines of the session's conduct
// ledger, not turns; a ref that cannot be placed (its message is not loaded,
// or the report's lists skip unavailable items) stays as the judge wrote it.
const ITEM_REF = /\b([tc])(\d{1,4})\b/g;

export function resolveTurnRefs(text, report, turnIndexOf, base = 0) {
    if (!text || report?.coverageGapCount > 0) return text || "";
    return String(text).replace(ITEM_REF, (ref, kind, n) => {
        const order = kind === "t" ? report?.targetOrder : report?.contextOrder;
        const id = order?.[Number(n) - 1];
        const turn = id ? turnIndexOf.get(id) : undefined;
        return turn == null ? ref : `T${base + turn}`;
    });
}

// Saved reports -> one ledger. Reports are read in window order, so their
// summaries read as the session's chapters; an overlapping re-review raises
// the same finding twice, so a finding with the same title over the same
// quotes from the same messages is listed once. Rows sort by the first turn
// their evidence lands on, so the timeline's lanes step down the strip.
// `base` is the record's turn base (recordTurnBase), for the turn refs.
export function buildLedger(reports = [], turns = [], { base = 0 } = {}) {
    const timeline = buildReportsTimeline(reports, turns);
    const turnIndexOf = new Map();
    turns.forEach((turn, i) => {
        const id = String(turn?.messageId || "").toLowerCase();
        if (id && !turnIndexOf.has(id)) turnIndexOf.set(id, i);
    });
    const prose = (text, report) => resolveTurnRefs(text, report, turnIndexOf, base);
    const placed = timeline.reports.filter((r) => r.placed);
    const window = placed.length
        ? {
            firstTurn: Math.min(...placed.map((r) => r.from)) + 1,
            lastTurn: Math.max(...placed.map((r) => r.through)) + 1,
            totalTurns: turns.length,
        }
        : null;
    const chapters = timeline.reports
        .filter((r) => r.summary)
        .map((r) => ({ key: r.id || r.reportId || String(r.index), summary: prose(r.summary, r), placed: r.placed, from: r.from, through: r.through }));

    // Counted per distinct finding, like the rows: a re-review over an
    // overlapping window repeats its findings, and a finding one report
    // withheld may be one another report admitted.
    const seen = new Set();
    const counted = new Set();
    const withheldKeys = new Set();
    const entries = [];
    let refuted = 0;
    let verifierError = false;
    for (const [reportIndex, report] of timeline.reports.entries()) {
        for (const [findingIndex, finding] of (report.findings || []).entries()) {
            const key = findingKey(finding);
            if (!counted.has(key)) {
                counted.add(key);
                for (const frame of [finding.policy, finding.norms, finding.intent]) {
                    if (frame?.claim?.result === "refuted") refuted += 1;
                }
            }
            if (finding.verifierStatus === "error") verifierError = true;
            if (!isActionableFinding(finding)) {
                if (isWithheldFinding(finding)) withheldKeys.add(key);
                continue;
            }
            if (seen.has(key)) continue;
            seen.add(key);
            const incident = ledgerIncident(finding, turns);
            entries.push({
                ...incident,
                key: incident.id || `${report.id || report.reportId || "report"}-${reportIndex}-${findingIndex}`,
                scopeKey: scopeKeyOf(report),
                firstTurn: incident.turns[0] ?? Number.POSITIVE_INFINITY,
                reportIndex,
                findingIndex,
            });
        }
    }
    const withheld = [...withheldKeys].filter((key) => !seen.has(key)).length;
    const incidents = entries.sort((a, b) => a.firstTurn - b.firstTurn
        || a.reportIndex - b.reportIndex
        || a.findingIndex - b.findingIndex);
    const temporalNotes = [...new Set(timeline.reports.map((r) => prose(r.temporalNote, r)).filter(Boolean))];
    return { chapters, window, incidents, withheld, refuted, verifierError, temporalNotes };
}

// Located citations -> signal-highlight entries (see signalHighlight.js's
// `block` loc surface). Only exact raw substrings of a text-ish block can be
// marked (markRanges does raw indexOf); anything else still navigates via its
// citation chip, it just doesn't get an in-text mark.
function evidenceHighlights(incidents, trace) {
    const locs = [];
    for (const inc of incidents || []) {
        for (const cit of inc.citations || []) {
            if (cit.verified === "missing" || !cit.locatedTurn) continue;
            const match = String(cit.quote || "").replace(/^["'\u201c\u201d\u2026\s]+/, "").replace(/["'\u201c\u201d\u2026\s]+$/, "");
            if (!match) continue;
            const blocks = trace?.turns?.[cit.locatedTurn - 1]?.blocks || [];
            for (let b = 0; b < blocks.length; b++) {
                const text = blocks[b]?.type === "text" ? blocks[b].text
                    : blocks[b]?.type === "tool_result" && typeof blocks[b].content === "string" ? blocks[b].content
                        : null;
                if (typeof text === "string" && text.includes(match)) {
                    locs.push({ surface: "block", turn: cit.locatedTurn - 1, block: b, match });
                    break;
                }
            }
        }
    }
    if (!locs.length) return null;
    return [{ command: "eval.citation", type: "eval.evidence", value: { locs } }];
}

export function backendEvidenceHighlights(reports, trace) {
    return evidenceHighlights(buildLedger(reports, trace?.turns || []).incidents, trace);
}

// The rollup's worst rung per axis, over the ledger's findings. A finding
// whose evidence the pipeline found partial counts one rung lower than it
// claims: its evidence was partly unfounded, so its rating is too. The
// pipeline's report header applies the same discount.
function discountRung(ladder, value, discount) {
    const i = ladder.indexOf(value);
    if (i < 0) return null;
    return ladder[Math.max(0, i - discount)];
}

export function ledgerRollup(incidents) {
    let behaviour = null;
    let content = null;
    for (const inc of incidents || []) {
        const d = inc.partial ? 1 : 0;
        const severity = discountRung(BEHAVIOUR_SEVERITIES, inc.behaviour?.severity, d);
        const sensitivity = discountRung(CONTENT_SENSITIVITIES, inc.content?.sensitivity, d);
        if (severity && BEHAVIOUR_SEVERITIES.indexOf(severity) > BEHAVIOUR_SEVERITIES.indexOf(behaviour)) behaviour = severity;
        if (sensitivity && CONTENT_SENSITIVITIES.indexOf(sensitivity) > CONTENT_SENSITIVITIES.indexOf(content)) content = sensitivity;
    }
    return { behaviour, content };
}

// -- timeline -------------------------------------------------------------------
// The whole trace as a density strip (tick height = turn volume), the reviewed
// windows bracketed, cited turns spiked in their finding's tone, and one
// aligned lane per finding with turn labels. Rendered in MEASURED PIXEL SPACE
// (no viewBox stretching): density ticks are bucketed, but every evidence
// spike and lane marker is placed at its turn's exact x through the same scale
// function, so the strip and the lanes cannot disagree. Renders on every
// loaded review -- a clean window still shows the density strip and the
// reviewed-window bracket, which is most of what "the judge read this much"
// means.
const TL = { pad: 3, ticksTop: 8, ticksH: 20, endLabelY: 39, laneStart: 48, laneGap: 11 };

// Consecutive cited turns collapse into one drawn span ([41,42] -> a single
// pill labelled 41-42) so a finding's evidence reads as a range, not
// confetti. `turns` is sorted unique.
function turnSegments(turns) {
    const segs = [];
    let lo = turns[0];
    let hi = turns[0];
    for (let i = 1; i < turns.length; i++) {
        if (turns[i] === hi + 1) { hi = turns[i]; continue; }
        segs.push([lo, hi]);
        lo = turns[i];
        hi = turns[i];
    }
    if (lo !== undefined) segs.push([lo, hi]);
    return segs;
}

// Geometry for one finding's lane. Pure, because eyeballing a chart is how
// this strip's layout bugs got shipped.
//
// ONE label per lane, at the lane's extent -- a label per segment collides
// whenever two cited turns land within a few pixels of each other, which on a
// thousand-turn strip is most of the time. The exact turns are enumerated by
// the finding's citation chips; the strip is for position. `format` prints a
// 1-based located turn as the number the ledger shows for it.
export function laneGeometry({ turns, x, width, pad, format = (turn) => turn }) {
    const segments = turnSegments(turns);
    const first = turns[0];
    const last = turns[turns.length - 1];
    const text = first === last ? String(format(first)) : `${format(first)}\u2013${format(last)}`;
    const est = text.length * 5.5 + 6;
    // Right of the lane by default; left when that would run off the strip.
    // When a lane spans nearly the whole width NEITHER side fits -- then the
    // label is dropped rather than drawn off-canvas or on top of its own
    // marker. A full-width bar already says "the whole window", and the
    // finding's citation chips carry the exact turns.
    const fitsRight = x(last) + 5 + est <= width - pad;
    const fitsLeft = x(first) - 5 - est >= pad;
    const label = fitsRight || fitsLeft ? text : null;
    const flip = !fitsRight;
    const labelX = flip ? x(first) - 5 : x(last) + 5;
    const labelSpan = !label
        ? [0, 0]
        : flip
            ? [x(first) - 5 - est, x(first) - 3]
            : [x(last) + 3, x(last) + 5 + est];

    // The hairline is drawn only where nothing else sits on it: run under a
    // marker or a label it reads as a strikethrough.
    const occupied = [
        ...segments.map(([lo, hi]) => [x(lo) - 3, x(hi) + 3]),
        labelSpan,
    ].sort((a, b) => a[0] - b[0]);
    const gaps = [];
    let cursor = pad;
    for (const [from, to] of occupied) {
        if (from - cursor > 2) gaps.push([cursor, from]);
        cursor = Math.max(cursor, to);
    }
    if (width - pad - cursor > 2) gaps.push([cursor, width - pad]);

    return { segments, label, labelX, anchor: flip ? "end" : "start", gaps };
}

const TONE_RANK = { calm: 0, warn: 1, danger: 2 };

// What a spike's hover says: the turn, the findings citing it, and what they
// quote there.
function SpikeTip({ turn, at }) {
    // `turn` arrives as printed: the record's number for it.
    return (
        <span className="grid gap-[5px] text-left">
            <span className="text-[12px] text-[color:var(--muted)]">
                {`Turn ${turn} \u00b7 ${at.titles.length} finding${at.titles.length === 1 ? "" : "s"}`}
            </span>
            <span className="grid gap-[3px] text-[11px] leading-[1.4] text-[color:var(--ink)]">
                {at.titles.map((title, i) => <span key={i}>{title}</span>)}
            </span>
            {at.quotes.length > 0 && (
                <span className="block max-h-[140px] overflow-hidden whitespace-pre-wrap break-words font-mono text-[10px] leading-[1.45] text-[color:var(--muted)]">
                    {at.quotes.join("\n")}
                </span>
            )}
        </span>
    );
}

function EvaluationTimeline({ turns, ledger, onJumpToTurn, hover = null, onHover = null, base = 0 }) {
    // A 1-based located turn as printed: its place in the record, from zero.
    const label = (turn) => base + turn - 1;
    const wrapRef = useRef(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        setWidth(el.clientWidth);
        if (typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(() => setWidth(el.clientWidth));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const total = turns.length;
    const sizes = useMemo(() => turns.map((t) => turnText(t).length), [turns]);
    const incidents = ledger.incidents.filter((i) => i.turns.length);
    if (!total) return null;

    const W = Math.floor(width);
    const height = incidents.length ? TL.laneStart + incidents.length * TL.laneGap : TL.endLabelY + 6;
    const reviewed = ledger.window;
    const first = reviewed?.firstTurn || 1;
    const last = reviewed?.lastTurn || total;
    const x = (turn) => (total <= 1 ? W / 2 : TL.pad + ((turn - 1) / (total - 1)) * (W - TL.pad * 2));
    // Hovering a lane, a spike or a ledger row dims everything else on the
    // strip, so a finding and the evidence it cites read as one thing.
    const dimmed = (keys) => !!hover && !keys.some((key) => hover.has(key));
    const enter = (keys) => (onHover ? () => onHover(keys) : undefined);
    const leave = onHover ? () => onHover(null) : undefined;

    let body = null;
    if (W >= 120) {
        const maxSize = sizes.reduce((max, size) => (size > max ? size : max), 1);
        // Density buckets at ~3px pitch -- background texture only; evidence
        // is drawn separately at exact positions.
        const bucketCount = Math.max(1, Math.min(total, Math.floor((W - TL.pad * 2) / 3)));
        const buckets = [];
        for (let b = 0; b < bucketCount; b++) {
            const lo = Math.floor((b * total) / bucketCount) + 1;
            const hi = Math.max(lo, Math.floor(((b + 1) * total) / bucketCount));
            let size = 0;
            for (let u = lo; u <= hi; u++) size = Math.max(size, sizes[u - 1] || 0);
            const h = 3 + Math.round((TL.ticksH - 3) * (Math.log(1 + size) / Math.log(1 + maxSize)));
            buckets.push({ xpos: (x(lo) + x(hi)) / 2, h, inWindow: hi >= first && lo <= last });
        }
        // Per cited turn: the loudest tone among the findings citing it, and
        // those findings with what they quote there, for the spike's hover.
        const cited = new Map();
        for (const inc of incidents) {
            const tone = incidentTone(inc);
            for (const t of inc.turns) {
                const at = cited.get(t) || { tone, keys: [], titles: [], quotes: [] };
                if (TONE_RANK[tone] > TONE_RANK[at.tone]) at.tone = tone;
                at.keys.push(inc.key);
                at.titles.push(inc.title);
                for (const c of inc.citations) {
                    if (c.locatedTurn === t && c.quote && !at.quotes.includes(c.quote)) at.quotes.push(c.quote);
                }
                cited.set(t, at);
            }
        }
        body = (
            <svg width={W} height={height} className="block" role="img" aria-label={`Timeline of ${total} turns; reviewed ${first}-${last}; ${incidents.length} findings marked`}>
                {reviewed && (
                    <>
                        <line x1={x(first)} y1="2.5" x2={x(last)} y2="2.5" stroke="var(--line-strong)" strokeWidth="1" />
                        <line x1={x(first)} y1="2.5" x2={x(first)} y2="6.5" stroke="var(--line-strong)" strokeWidth="1" />
                        <line x1={x(last)} y1="2.5" x2={x(last)} y2="6.5" stroke="var(--line-strong)" strokeWidth="1" />
                    </>
                )}
                {buckets.map((b, i) => (
                    <rect key={i} x={b.xpos - 1} y={TL.ticksTop + TL.ticksH - b.h} width="2" height={b.h} fill="var(--line-strong)" opacity={b.inWindow ? 0.85 : 0.3} />
                ))}
                {[...cited.entries()].map(([t, at]) => (
                    <HoverTooltip key={`s${t}`} wide maxWidth={420} placement="top" label={<SpikeTip turn={label(t)} at={at} />}>
                        <g
                            data-review-timeline-evidence=""
                            tabIndex={0}
                            aria-label={`Turn ${label(t)}: ${at.titles.length} finding${at.titles.length === 1 ? "" : "s"}`}
                            opacity={dimmed(at.keys) ? 0.25 : 1}
                            className={onJumpToTurn ? "cursor-pointer" : undefined}
                            onClick={onJumpToTurn ? () => onJumpToTurn(t - 1) : undefined}
                            onMouseEnter={enter(at.keys)}
                            onMouseLeave={leave}
                            onFocus={enter(at.keys)}
                            onBlur={leave}
                        >
                            {/* A 2px spike is too thin to hover; the clear
                                rect widens the target without drawing. */}
                            <rect x={x(t) - 3.5} y={TL.ticksTop - 2} width="7" height={TL.ticksH + 4} fill="transparent" />
                            <rect x={x(t) - 1} y={TL.ticksTop - 2} width="2" height={TL.ticksH + 4} fill={SEVERITY_COLOR[at.tone]} />
                        </g>
                    </HoverTooltip>
                ))}
                <text x={TL.pad} y={TL.endLabelY} fontSize="9" fill="var(--muted-soft)">{label(1)}</text>
                <text x={W - TL.pad} y={TL.endLabelY} fontSize="9" fill="var(--muted-soft)" textAnchor="end">{label(total)}</text>
                {incidents.map((inc, i) => {
                    const y = TL.laneStart + i * TL.laneGap;
                    const tone = incidentTone(inc);
                    const lane = laneGeometry({ turns: inc.turns, x, width: W, pad: TL.pad, format: label });
                    return (
                        <g
                            key={inc.key || i}
                            data-review-timeline-lane=""
                            data-linked={hover?.has(inc.key) ? "true" : undefined}
                            opacity={dimmed([inc.key]) ? 0.3 : 1}
                            onMouseEnter={enter([inc.key])}
                            onMouseLeave={leave}
                        >
                            {/* The whole lane is the hover target, not just its
                                4px markers. */}
                            <rect x={TL.pad} y={y - TL.laneGap / 2} width={Math.max(0, W - TL.pad * 2)} height={TL.laneGap} fill="transparent" />
                            {lane.gaps.map(([from, to], gi) => (
                                <line key={gi} x1={from} y1={y} x2={to} y2={y} stroke="var(--line-soft)" strokeWidth="0.6" />
                            ))}
                            {lane.segments.map(([lo, hi]) => (
                                <rect
                                    key={lo}
                                    x={x(lo) - 2}
                                    y={y - 2}
                                    width={Math.max(4, x(hi) - x(lo) + 4)}
                                    height="4"
                                    rx="2"
                                    fill={SEVERITY_COLOR[tone]}
                                    className={onJumpToTurn ? "cursor-pointer" : undefined}
                                    onClick={onJumpToTurn ? () => onJumpToTurn(lo - 1) : undefined}
                                />
                            ))}
                            {lane.label && (
                                <text
                                    x={lane.labelX}
                                    y={y + 3}
                                    fontSize="9"
                                    fill="var(--muted-soft)"
                                    textAnchor={lane.anchor}
                                >
                                    {lane.label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
        );
    }
    return <div ref={wrapRef} className="w-full min-w-0" data-backend-review-timeline="">{body}</div>;
}

// -- finding row ----------------------------------------------------------------

function FrameDot({ kind }) {
    if (kind === "dash") return <span data-dot="dash" className="text-[color:var(--muted-soft)] text-[11px] leading-none">{"\u2013"}</span>;
    if (kind === "ring") {
        return <span data-dot="ring" className="inline-block h-[8px] w-[8px] rounded-full border border-[color:var(--line-strong)]" />;
    }
    // "norm" is the descriptive frame's filled dot -- the house accent, not a
    // severity color, so observed practice never reads as an alarm.
    if (kind === "norm") {
        return <span data-dot="norm" className="inline-block h-[8px] w-[8px] rounded-full bg-[color:var(--green)]" />;
    }
    return <span data-dot={kind} className="inline-block h-[8px] w-[8px] rounded-full" style={{ background: SEVERITY_COLOR[kind] || SEVERITY_COLOR.calm }} />;
}

function AxisLine({ label, rung, category, definition, tone }) {
    const head = rung ? `${label.toLowerCase()} ${rung}` : label.toLowerCase();
    return (
        <OverflowTooltip
            text={definition ? `${head} \u00b7 ${category || "\u2013"} - ${definition}` : `${head} \u00b7 ${category || "\u2013"}`}
            className="block text-[11px] leading-[1.3]"
        >
            <span className="text-[color:var(--muted)]">{label}</span>{" "}
            {/* The rung, spelled out -- three tone colours cannot express five
                ladder steps, so the word carries the ordinal. Only the rung
                wears the severity colour: it is the measurement, while the
                category is the judge's descriptive label, kept neutral and
                dot-separated so the two never read as one phrase. */}
            {rung && <span className="tracking-[0.03em]" style={{ color: SEVERITY_COLOR[tone] }}>{rung}</span>}
            {rung && <span className="text-[color:var(--muted-soft)]">{" \u00b7 "}</span>}
            <span className="text-[color:var(--ink)]">{category || "\u2013"}</span>
        </OverflowTooltip>
    );
}

// How much of the turn to show behind a citation. Generous enough to read the
// quote in context, bounded so a 4k-char tool result doesn't become a
// full-screen tooltip.
const TURN_TOOLTIP_BEFORE = 700;
const TURN_TOOLTIP_AFTER = 900;

// The cited turn's own text, windowed around the quote and split so the quote
// can render highlighted in place. Null when the turn isn't loaded; a quote
// that can't be located in it still gets its turn shown, just without the
// mark.
export function citationContext(turnTexts, citation) {
    const idx = (citation?.locatedTurn || 0) - 1;
    const full = turnTexts?.[idx];
    if (typeof full !== "string" || !full) return null;
    const needle = String(citation?.quote || "").replace(/^["'\u201c\u201d\u2026\s]+/, "").replace(/["'\u201c\u201d\u2026\s]+$/, "");
    const at = needle ? full.indexOf(needle) : -1;
    if (at < 0) {
        const head = full.slice(0, TURN_TOOLTIP_BEFORE + TURN_TOOLTIP_AFTER);
        return { before: head, match: "", after: "", clippedHead: false, clippedTail: head.length < full.length };
    }
    const from = Math.max(0, at - TURN_TOOLTIP_BEFORE);
    const to = Math.min(full.length, at + needle.length + TURN_TOOLTIP_AFTER);
    return {
        before: full.slice(from, at),
        match: full.slice(at, at + needle.length),
        after: full.slice(at + needle.length, to),
        clippedHead: from > 0,
        clippedTail: to < full.length,
    };
}

function CitationChip({ citation, turnTexts, onJumpToTurn, base = 0 }) {
    const printed = citation.locatedTurn != null ? base + citation.locatedTurn - 1 : null;
    const placed = citation.locatedTurn != null && citation.verified !== "missing";
    // Not on a loaded turn: either an earlier page of the record carries the
    // cited message, or the saved quote matches nothing the reader can see.
    const earlier = !placed && !!citation.unloaded;
    const unmatched = !placed && !earlier;
    const clickable = placed && !!onJumpToTurn;
    const label = (
        <>
            <span className="text-[color:var(--muted)] shrink-0">
                {placed ? `Turn ${printed}` : earlier ? "Earlier" : "Not located"}
            </span>
            <span className="truncate min-w-0">{`\u201c${citation.quote}\u201d`}</span>
            {placed && (
                <span className="shrink-0" style={{ color: "var(--green)" }}>
                    {citation.verified === "relocated" ? "\u2713 moved" : "\u2713"}
                </span>
            )}
        </>
    );
    const cls = [
        "inline-flex max-w-full min-w-0 items-baseline gap-[5px] rounded-[5px] border px-[6px] py-[2px] text-[11px] leading-[1.2]",
        unmatched ? "border-[color:var(--warn,#c08a2d)] text-[color:var(--muted)]"
            : earlier ? "border-[color:var(--line)] text-[color:var(--muted)]"
                : "border-[color:var(--line)] bg-[color:var(--bg)] text-[color:var(--ink)]",
        clickable ? "cursor-pointer hover:border-[color:var(--ink)]" : "",
    ].join(" ");
    if (!placed) {
        return (
            <HoverTooltip
                wide
                placement="top"
                label={earlier
                    ? "The cited message is earlier in the conversation than the turns loaded here."
                    : "This quote does not match any loaded turn."}
            >
                <span className={cls} tabIndex={0}>{label}</span>
            </HoverTooltip>
        );
    }
    const chip = !clickable
        ? <span className={cls}>{label}</span>
        : (
            <button type="button" data-finding-cite="" className={cls} onClick={() => onJumpToTurn(citation.locatedTurn - 1)}>
                {label}
            </button>
        );
    const ctx = citationContext(turnTexts, citation);
    if (!ctx) return chip;
    // The turn behind the citation, so the quote can be read in context rather
    // than taken on trust.
    const tip = (
        <span className="grid gap-[5px]">
            <span className="text-[12px] text-[color:var(--muted)]">Turn {printed}</span>
            <span className="block max-h-[300px] overflow-hidden font-mono text-[10px] leading-[1.45] whitespace-pre-wrap break-words text-[color:var(--muted)]">
                {ctx.clippedHead ? "\u2026" : ""}
                {ctx.before}
                {ctx.match && (
                    <span
                        className="text-[color:var(--ink)]"
                        style={{
                            backgroundColor: "color-mix(in srgb, var(--amber,#b8860b) 22%, transparent)",
                            boxShadow: "inset 0 -2px 0 var(--amber,#b8860b)",
                        }}
                    >
                        {ctx.match}
                    </span>
                )}
                {ctx.after}
                {ctx.clippedTail ? "\u2026" : ""}
            </span>
        </span>
    );
    return <HoverTooltip wide maxWidth={560} placement="top" label={tip}>{chip}</HoverTooltip>;
}

// Shared row template so the ledger's column header and every finding card lay
// their dot columns on the same grid lines: one 56px column per document the
// review weighed (policy, norms, intent), so the header labels fit INSIDE
// their cells. A grid cell's overflow is visible, so a
// too-narrow column silently widens every ancestor's scroll width and puts a
// horizontal scrollbar on the whole panel.
//
// The columns appear only once the ledger leaves a title about 300px to read
// in; narrower, each row states its verdicts in words under its title and the
// title takes the full width. Container queries, not viewport ones: the
// Context panel is narrow on any screen.
//
// Every variant is a LITERAL class string -- Tailwind's JIT scans source text,
// so a runtime-joined template would never be generated. Keep these in step
// with the cells the header and IncidentRow render: a template one column
// short doesn't overflow, it silently wraps the last frame onto a second row,
// which reads as a stray dot rather than a bug.
const ROW_LAYOUT = {
    0: {
        cols: "[grid-template-columns:12px_minmax(0,1fr)]",
        header: "hidden",
        dot: "hidden",
        words: "",
    },
    1: {
        cols: "[grid-template-columns:12px_minmax(0,1fr)] @min-[400px]:[grid-template-columns:12px_minmax(0,1fr)_56px]",
        header: "hidden @min-[400px]:grid",
        dot: "hidden @min-[400px]:block",
        words: "@min-[400px]:hidden",
    },
    2: {
        cols: "[grid-template-columns:12px_minmax(0,1fr)] @min-[460px]:[grid-template-columns:12px_minmax(0,1fr)_56px_56px]",
        header: "hidden @min-[460px]:grid",
        dot: "hidden @min-[460px]:block",
        words: "@min-[460px]:hidden",
    },
    3: {
        cols: "[grid-template-columns:12px_minmax(0,1fr)] @min-[520px]:[grid-template-columns:12px_minmax(0,1fr)_56px_56px_56px]",
        header: "hidden @min-[520px]:grid",
        dot: "hidden @min-[520px]:block",
        words: "@min-[520px]:hidden",
    },
};

// A clause ref alone ("0.2") is an index, not information. Resolve it to its
// section heading and its own text so the row can say what the clause actually
// is. Sections are the parent clause ("0.2" -> "0"), which the constitution
// parser already captures as its own numbered entry.
export function describeClause(clauses, ref) {
    if (!ref) return null;
    const self = (clauses || []).find((c) => c.ref === ref);
    const parentRef = ref.includes(".") ? ref.slice(0, ref.lastIndexOf(".")) : null;
    const parent = parentRef ? (clauses || []).find((c) => c.ref === parentRef) : null;
    return {
        ref,
        text: self?.text || null,
        section: parent?.text || null,
    };
}
export const rowFrameCount = (hasPolicy, hasNorms, hasIntents) =>
    (hasPolicy ? 1 : 0) + (hasNorms ? 1 : 0) + (hasIntents ? 1 : 0);

// Every listed row is a mismatch, so each one can say which mismatch put it
// there -- the reader should never have to work out why they are looking at it.
function reasonLabel(reason) {
    switch (reason.kind) {
        case "dead_letter":
            return `Typical here. Conflicts with clause ${reason.clause}.`;
        case "coverage_gap":
            return "Risk not covered by policy.";
        case "unwritten":
            return "Observed practice not covered by policy.";
        case "injection":
            return "A prompt injection reached this trace.";
        default:
            return "Works against an organizational objective.";
    }
}

// The cross-frame signatures are findings about the ORGANIZATION rather than
// the trace, so they get the warn outline.
const CROSS_FRAME_REASONS = new Set(["dead_letter", "coverage_gap", "unwritten"]);

// Whether the reason is worth a line of its own. `risk` and `sensitivity` are
// already spelled out by the axis rungs, `policy` by the clause line above and
// a norm deviation by the norms verdict. Off-mission and injection have
// nowhere else to appear beside the dot columns: the intent frame is only a
// dot there, and an injection has no frame at all. (A narrow row's words state
// the intent verdict, so the off-mission line hides there.)
export function reasonWorthStating(kind) {
    return CROSS_FRAME_REASONS.has(kind) || kind === "off-mission" || kind === "injection";
}

// A frame's claim verdict, as the attestation line's closing words.
function claimSuffix(claim) {
    if (!claim) return "";
    return claim.holds ? " \u00b7 claim upheld" : " \u00b7 claim refuted on verification";
}

// A resolved profile item, quoted under the attestation that names it. Nested
// content sits on the page background rather than behind a left rule.
function DocumentQuote({ children }) {
    return (
        <span className="min-w-0 break-words rounded-[6px] bg-[color:var(--bg)] px-[8px] py-[4px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
            {children}
        </span>
    );
}

function profileItemText(item) {
    return [item.title, item.description].filter(Boolean).join(". ");
}

const NO_DOCUMENTS = ledgerDocuments(null);

const DOT_INK = { danger: SEVERITY_COLOR.danger, warn: SEVERITY_COLOR.warn, norm: "var(--green)", ring: "var(--muted)" };

// Each document's verdict on a finding: the dot its column draws, the words the
// narrow layout prints under the title, and the sentence the dot's hover gives.
// A written clause broken is the danger colour and any other objection the
// warn colour, as on the clause line and in the rollup; the house accent
// vouches, a ring saw no problem, and a dash had no view. A refuted objection
// is a ring: the verifier took it back.
export function frameVerdicts(incident, docs, { hasPolicy, hasNorms, hasIntents }) {
    const titled = (list, ref) => {
        const title = ref ? list.find((n) => n.ref === ref)?.title : "";
        return title ? `${ref} (${title})` : ref;
    };
    const upheld = (claim) => (claim?.holds ? " Upheld on verification." : "");
    const out = [];
    if (hasPolicy) {
        const p = incident.policy;
        const ref = p?.clause || "";
        const clause = ref ? describeClause(docs.clauses, ref) : null;
        const heading = clause?.section || clause?.text || "";
        const name = ref ? `clause ${ref}${heading ? ` (${heading})` : ""}` : "a clause";
        if (!p) out.push({ frame: "Policy", dot: "dash", tip: "Judged without a policy." });
        else if (p.status === "no_view") out.push({ frame: "Policy", dot: "dash", tip: "No policy clause covers this." });
        else if (p.status === "violation" && incident.policyClaim?.holds !== false) {
            out.push({ frame: "Policy", dot: "danger", word: ref ? `breaks ${ref}` : "breaks policy", tip: `Breaks ${name}.${upheld(incident.policyClaim)}` });
        } else if (p.status === "violation") {
            out.push({ frame: "Policy", dot: "ring", word: "objection refuted", tip: `An objection under ${name} was refuted on verification.` });
        } else out.push({ frame: "Policy", dot: "ring", word: "within policy", tip: ref ? `Within policy under ${name}.` : "Within policy." });
    }
    if (hasNorms) {
        const n = incident.norms;
        const ref = n?.norm || "";
        if (!n || n.status === "no_view") out.push({ frame: "Norms", dot: "dash", tip: "No observed norm speaks to this." });
        else if (n.status === "typical") {
            out.push({ frame: "Norms", dot: "norm", word: "routine here", tip: `Routine for this org${ref ? `: ${titled(docs.norms, ref)}` : ""}.` });
        } else if (incident.normsClaim?.holds !== false) {
            out.push({ frame: "Norms", dot: "warn", word: ref ? `departs from ${ref}` : "unusual here", tip: `${ref ? `Departs from ${titled(docs.norms, ref)}` : "Unusual for this org"}.${upheld(incident.normsClaim)}` });
        } else out.push({ frame: "Norms", dot: "ring", word: "departure refuted", tip: `A departure from ${ref || "a norm"} was refuted on verification.` });
    }
    if (hasIntents) {
        const i = incident.intent;
        const ref = i?.intent || "";
        if (!i || i.status === "no_view") out.push({ frame: "Intent", dot: "dash", tip: "No objective speaks to this." });
        else if (i.status === "advances") {
            out.push({ frame: "Intent", dot: "norm", word: ref ? `advances ${ref}` : "advances an objective", tip: `Advances ${ref ? titled(docs.intents, ref) : "an org objective"}.` });
        } else if (i.status === "counter" && incident.intentClaim?.holds !== false) {
            out.push({ frame: "Intent", dot: "warn", word: ref ? `works against ${ref}` : "works against an objective", tip: `Works against ${ref ? titled(docs.intents, ref) : "an org objective"}.${upheld(incident.intentClaim)}` });
        } else if (i.status === "counter") {
            out.push({ frame: "Intent", dot: "ring", word: "objection refuted", tip: `An objection under ${ref || "an objective"} was refuted on verification.` });
        } else out.push({ frame: "Intent", dot: "ring", word: "tangential", tip: "Unrelated to the org's objectives." });
    }
    return out;
}

// The turns a finding's located evidence spans, as the Lanes print them.
function turnSpan(turns, base = 0) {
    if (!turns.length) return null;
    const first = base + turns[0] - 1;
    const last = base + turns[turns.length - 1] - 1;
    return first === last ? `T${first}` : `T${first}\u2013T${last}`;
}

const ROW_CLASS = "rounded-[10px] border px-[8px] py-[6px] min-w-0";
const ROW_REST = "border-[color:var(--line)] bg-[color:var(--control-bg)]";
// Linked from the timeline: its lane or a spike it cites is under the pointer.
const ROW_LINKED = "border-[color:color-mix(in_srgb,var(--muted)_55%,transparent)] bg-[color:var(--control-raised-bg)]";

function IncidentRow({ incident, docs = NO_DOCUMENTS, turnTexts, frames, linked = false, onHover = null, onJumpToTurn, base = 0 }) {
    const [open, setOpen] = useState(false);
    const { hasPolicy } = frames;
    const layout = ROW_LAYOUT[frames.count];
    const verdicts = frameVerdicts(incident, docs, frames);
    const worded = verdicts.filter((v) => v.word);
    const policy = incident.policy;
    const normAttestation = incident.norms;
    const clauseInfo = policy?.clause ? describeClause(docs.clauses, policy.clause) : null;
    const clauseText = clauseInfo?.text || null;
    const normText = normAttestation?.norm ? docs.norms.find((n) => n.ref === normAttestation.norm) : null;
    const intentAttestation = incident.intent;
    const intentText = intentAttestation?.intent ? docs.intents.find((n) => n.ref === intentAttestation.intent) : null;
    const reason = incidentReason(incident);
    const asserted = assertedRungs(incident);
    const policyObjects = policy?.status === "violation" && incident.policyClaim?.holds !== false;
    const intentObjects = intentAttestation?.status === "counter" && incident.intentClaim?.holds !== false;
    const normObjects = normAttestation?.status === "deviation" && incident.normsClaim?.holds !== false;
    const turnLabel = turnSpan(incident.turns, base);
    const link = onHover ? () => onHover([incident.key]) : undefined;
    const unlink = onHover ? () => onHover(null) : undefined;
    return (
        <div
            className={`${ROW_CLASS} ${linked ? ROW_LINKED : ROW_REST}`}
            data-ledger-finding={incident.id || ""}
            data-linked={linked ? "true" : undefined}
            onMouseEnter={link}
            onMouseLeave={unlink}
        >
            <div
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
                onFocus={link}
                onBlur={unlink}
                className={`grid ${layout.cols} items-start gap-x-[6px] py-[2px] min-w-0 cursor-pointer`}
            >
                <span className="text-[10px] leading-[1.5] text-[color:var(--muted-soft)]">{open ? "\u25be" : "\u25b8"}</span>
                <span className="min-w-0">
                    <span className="flex items-baseline gap-[6px] min-w-0">
                        <OverflowTooltip text={incident.title} lines={2} className="block min-w-0 flex-1 text-[12px] leading-[1.3] text-[color:var(--ink)]" />
                        {turnLabel && (
                            <span className="shrink-0 font-mono text-[10px] leading-[1.4] text-[color:var(--muted)]" data-finding-turns="">{turnLabel}</span>
                        )}
                        {incident.partial && (
                            <HoverTooltip wide placement="top" label="Some of this finding's quotes could not be verified against the conversation. Its rating counts one level lower in the rollup.">
                                <span className="shrink-0 rounded-[5px] border px-[4px] text-[10px] leading-[1.4]" style={{ color: SEVERITY_COLOR.warn, borderColor: SEVERITY_COLOR.warn }}>partial evidence</span>
                            </HoverTooltip>
                        )}
                        {incident.unplaced && (
                            <HoverTooltip
                                wide
                                placement="top"
                                label={incident.earlier
                                    ? "Its evidence is in messages earlier than the turns loaded here, so it has no lane on the timeline."
                                    : "None of its quotes match a loaded turn, so it has no lane on the timeline."}
                            >
                                <span className="shrink-0 rounded-[5px] border border-[color:var(--line)] px-[4px] text-[10px] leading-[1.4] text-[color:var(--muted)]">
                                    {incident.earlier ? "earlier" : "not located"}
                                </span>
                            </HoverTooltip>
                        )}
                    </span>
                    {incident.behaviour && (
                        <AxisLine label="Behaviour" rung={asserted.severity} category={incident.behaviour.category} definition={incident.behaviour.definition} tone={behaviourTone(asserted.severity)} />
                    )}
                    {incident.content && (
                        <AxisLine label="Content" rung={asserted.sensitivity} category={incident.content.category} definition={incident.content.definition} tone={contentTone(asserted.sensitivity)} />
                    )}
                    {/* The clause named, not just numbered -- a bare ref in a
                        narrow column tells the reader nothing. */}
                    {hasPolicy && policy && policy.status !== "no_view" && clauseInfo && (
                        <OverflowTooltip
                            text={`clause ${clauseInfo.ref}${clauseInfo.section ? ` \u00b7 ${clauseInfo.section}` : ""}${clauseInfo.text ? ` - ${clauseInfo.text}` : ""}`}
                            className="block text-[11px] leading-[1.3]"
                        >
                            <span className="text-[color:var(--muted)]">Clause {clauseInfo.ref}</span>{" "}
                            <span style={{ color: policyObjects ? SEVERITY_COLOR.danger : "var(--muted)" }}>
                                {clauseInfo.section || clauseInfo.text || "\u2013"}
                            </span>
                        </OverflowTooltip>
                    )}
                    {/* Only a verdict says no clause covers it: a finding
                        judged before the policy existed carries no frame. And
                        the coverage-gap chip below already says it. */}
                    {hasPolicy && policy?.status === "no_view" && reason?.kind !== "coverage_gap" && (
                        <span className="block text-[11px] leading-[1.3]">
                            <span className="text-[color:var(--muted)]">Clause</span>{" "}
                            <span style={{ color: SEVERITY_COLOR.warn }}>none covers this</span>
                        </span>
                    )}
                    {/* Narrow: the verdicts the dot columns would carry, in
                        words, so the title keeps the width. */}
                    {worded.length > 0 && (
                        <span className={`${layout.words} block text-[11px] leading-[1.3]`} data-frame-words="">
                            {worded.map((v, i) => (
                                <Fragment key={v.frame}>
                                    {i > 0 && <span className="text-[color:var(--muted-soft)]">{" \u00b7 "}</span>}
                                    <span className="text-[color:var(--muted)]">{v.frame}</span>{" "}
                                    <span style={{ color: DOT_INK[v.dot] }}>{v.word}</span>
                                </Fragment>
                            ))}
                        </span>
                    )}
                    {reason && reasonWorthStating(reason.kind) && (CROSS_FRAME_REASONS.has(reason.kind) ? (
                        <span className="mt-[3px] inline-flex max-w-full min-w-0 rounded-[5px] border px-[5px] py-[1px] text-[10px] leading-[1.3]" style={{ color: SEVERITY_COLOR.warn, borderColor: SEVERITY_COLOR.warn }}>
                            <span className="truncate min-w-0">{reasonLabel(reason)}</span>
                        </span>
                    ) : (
                        <OverflowTooltip
                            text={reasonLabel(reason)}
                            className={`mt-[2px] text-[10px] leading-[1.3] text-[color:var(--muted-soft)] ${reason.kind === "off-mission" && worded.some((v) => v.frame === "Intent") ? layout.dot : "block"}`}
                        />
                    ))}
                </span>
                {verdicts.map((v) => (
                    <span key={v.frame} className={`${layout.dot} pt-[3px] text-center`}>
                        <HoverTooltip wide placement="top" label={`${v.frame}: ${v.tip}`}>
                            <span className="inline-flex" data-frame-dot={v.frame}>
                                <FrameDot kind={v.dot} />
                            </span>
                        </HoverTooltip>
                    </span>
                ))}
            </div>
            {open && (
                // Four groups -- the judge's reasoning, its own vocabulary, the
                // documents' verdicts, evidence -- separated by hairlines so the
                // eye can tell where one kind of information ends. The sibling
                // variant draws a divider only between groups that rendered.
                <div className="mt-[6px] grid gap-[9px] border-t border-[color:var(--line-soft)] pt-[8px] pb-[2px] min-w-0 [&>div+div]:border-t [&>div+div]:border-[color:var(--line-soft)] [&>div+div]:pt-[9px]">
                    {incident.rationale && (
                        <div className="min-w-0">
                            <span className="block min-w-0 break-words text-[11px] leading-[1.45] text-[color:var(--ink)]">{incident.rationale}</span>
                        </div>
                    )}
                    {(incident.behaviour?.definition || incident.content?.definition) && (
                        <div className="grid gap-[4px] min-w-0">
                            {incident.behaviour?.definition && (
                                <span className="min-w-0 break-words text-[11px] leading-[1.4] text-[color:var(--muted)]">
                                    Judge definition: <span className="text-[color:var(--ink)]">{incident.behaviour.category}</span>. {incident.behaviour.definition}
                                </span>
                            )}
                            {incident.content?.definition && (
                                <span className="min-w-0 break-words text-[11px] leading-[1.4] text-[color:var(--muted)]">
                                    Judge definition: <span className="text-[color:var(--ink)]">{incident.content.category}</span>. {incident.content.definition}
                                </span>
                            )}
                        </div>
                    )}
                    {(policy || normAttestation || intentAttestation) && (
                        <div className="grid gap-[4px] min-w-0">
                            {policy && policy.status !== "no_view" && (
                                <span className="min-w-0 break-words text-[11px] leading-[1.4]" style={{ color: policyObjects ? SEVERITY_COLOR.danger : "var(--muted)" }}>
                                    {policy.status === "violation" ? "Against policy" : "Within policy"}
                                    {policy.clause ? ` \u00b7 clause ${policy.clause}` : ""}
                                    {policy.note ? ` - ${policy.note}` : ""}
                                    {claimSuffix(incident.policyClaim)}
                                </span>
                            )}
                            {policy?.status === "no_view" && (
                                <span className="min-w-0 break-words text-[11px] leading-[1.4]" style={{ color: SEVERITY_COLOR.warn }}>
                                    No policy clause covers this.{policy.note ? ` ${policy.note}` : " Consider adding one."}
                                </span>
                            )}
                            {clauseText && (
                                <DocumentQuote>
                                    CLAUSE {policy.clause}{clauseInfo.section ? ` \u00b7 ${clauseInfo.section}` : ""} - {clauseText}
                                </DocumentQuote>
                            )}
                            {normAttestation && normAttestation.status !== "no_view" && (
                                <span className="min-w-0 break-words text-[11px] leading-[1.4]" style={{ color: normObjects ? SEVERITY_COLOR.warn : "var(--muted)" }}>
                                    {normAttestation.status === "typical" ? "typical for this org" : "atypical for this org"}
                                    {normAttestation.norm ? ` \u00b7 norm ${normAttestation.norm}` : ""}
                                    {normAttestation.note ? ` - ${normAttestation.note}` : ""}
                                    {claimSuffix(incident.normsClaim)}
                                </span>
                            )}
                            {intentAttestation && intentAttestation.status !== "no_view" && (
                                <span className="min-w-0 break-words text-[11px] leading-[1.4]" style={{ color: intentObjects ? SEVERITY_COLOR.warn : "var(--muted)" }}>
                                    {intentAttestation.status === "advances" ? "advances an org objective"
                                        : intentAttestation.status === "counter" ? "works against an org objective"
                                            : "unrelated to an organizational objective"}
                                    {intentAttestation.intent ? ` \u00b7 intent ${intentAttestation.intent}` : ""}
                                    {intentAttestation.note ? ` - ${intentAttestation.note}` : ""}
                                    {claimSuffix(incident.intentClaim)}
                                </span>
                            )}
                            {intentText && (
                                <DocumentQuote>
                                    INTENT {intentText.ref}{intentText.weight ? ` [${intentText.weight}]` : ""} - {profileItemText(intentText)}
                                    {intentText.basis ? ` \u00b7 basis: ${intentText.basis}` : ""}
                                </DocumentQuote>
                            )}
                            {normText && (
                                <DocumentQuote>
                                    NORM {normText.ref}{normText.prevalence ? ` [${normText.prevalence}]` : ""} - {profileItemText(normText)}
                                    {normText.basis ? ` \u00b7 basis: ${normText.basis}` : ""}
                                </DocumentQuote>
                            )}
                        </div>
                    )}
                    {incident.citations.length > 0 && (
                        <div className="flex flex-wrap gap-[5px] min-w-0">
                            {incident.citations.map((c, i) => (
                                <CitationChip key={i} citation={c} turnTexts={turnTexts} onJumpToTurn={onJumpToTurn} base={base} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// -- review profile lines -------------------------------------------------------
// The documents the reviews were judged against, read from the review profile.
// Written policy and observed practice are shown apart and never merged: the
// gap between them is what the ledger's cross-frame signatures read.

const PREVALENCE_LABEL = {
    universal: "universal",
    common: "common",
    occasional: "occasional",
    rare: "rare",
};

const LINE_CLASS = "flex items-baseline gap-[6px] min-w-0 text-[11px] leading-[1.3] text-[color:var(--muted)]";

function Disclosure({ open, onToggle, children }) {
    return (
        <button type="button" aria-expanded={open} onClick={onToggle} className="flex min-w-0 items-baseline gap-[4px] cursor-pointer text-left hover:text-[color:var(--ink)]">
            <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">{open ? "\u25be" : "\u25b8"}</span>
            <span className="min-w-0 truncate">{children}</span>
        </button>
    );
}

function DocumentItem({ refLabel, text, meta }) {
    return (
        <span className="grid [grid-template-columns:26px_minmax(0,1fr)] gap-x-[6px] min-w-0">
            <span className="text-[10px] leading-[1.5] text-[color:var(--muted-soft)]">{refLabel}</span>
            <span className="min-w-0">
                <span className="block min-w-0 break-words text-[11px] leading-[1.4] text-[color:var(--ink)]">{text}</span>
                {meta && <span className="block min-w-0 break-words text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">{meta}</span>}
            </span>
        </span>
    );
}

function PolicyLine({ clauses }) {
    const [open, setOpen] = useState(false);
    const label = <span className="shrink-0 text-[color:var(--muted-soft)]">Policy</span>;
    if (!clauses.length) {
        return (
            <span className={LINE_CLASS}>
                {label}
                <span className="min-w-0 truncate">none written</span>
            </span>
        );
    }
    return (
        <span className="grid gap-[4px] min-w-0">
            <span className={LINE_CLASS}>
                {label}
                <Disclosure open={open} onToggle={() => setOpen((v) => !v)}>
                    {clauses.length} clause{clauses.length === 1 ? "" : "s"}
                </Disclosure>
            </span>
            {open && (
                <span className={`${CARD_CLASS} grid gap-[4px]`}>
                    {clauses.map((c) => <DocumentItem key={c.ref} refLabel={c.ref} text={c.text} />)}
                </span>
            )}
        </span>
    );
}

function NormsLine({ docs }) {
    const [open, setOpen] = useState(false);
    const label = (
        <HoverTooltip
            wide
            placement="top"
            label="Observed practices derived from trace analysis. They remain separate from written policy so conflicts and coverage gaps stay visible."
        >
            <span tabIndex={0} className="shrink-0 cursor-help text-[color:var(--muted-soft)]">Norms</span>
        </HoverTooltip>
    );
    const { norms, intents, summary } = docs;
    if (!norms.length && !intents.length) {
        return (
            <span className={LINE_CLASS}>
                {label}
                <span className="min-w-0 truncate">none discovered</span>
            </span>
        );
    }
    return (
        <span className="grid gap-[4px] min-w-0">
            <span className={LINE_CLASS}>
                {label}
                <Disclosure open={open} onToggle={() => setOpen((v) => !v)}>
                    {intents.length} intent{intents.length === 1 ? "" : "s"}{" \u00b7 "}{norms.length} norm{norms.length === 1 ? "" : "s"}
                </Disclosure>
            </span>
            {open && (
                <span className={`${CARD_CLASS} grid gap-[5px]`}>
                    {summary && (
                        <span className="min-w-0 break-words text-[11px] leading-[1.45] text-[color:var(--muted)]">{summary}</span>
                    )}
                    {/* Two documents, shown apart: what the org is trying to
                        achieve, and how it works. Merging them is exactly what
                        would erase the comparisons the frames exist to make. */}
                    {intents.length > 0 && (
                        <span className={`grid gap-[4px] min-w-0 ${summary ? "border-t border-[color:var(--line-soft)] pt-[5px]" : ""}`}>
                            <span className="text-[12px] text-[color:var(--muted)]">{"Intents \u00b7 what this org is trying to achieve"}</span>
                            {intents.map((n) => (
                                <DocumentItem
                                    key={n.ref}
                                    refLabel={n.ref}
                                    text={profileItemText(n)}
                                    meta={[n.weight, n.basis].filter(Boolean).join(" \u00b7 ")}
                                />
                            ))}
                        </span>
                    )}
                    {norms.length > 0 && (
                        <span className={`grid gap-[4px] min-w-0 ${summary || intents.length ? "border-t border-[color:var(--line-soft)] pt-[5px]" : ""}`}>
                            <span className="text-[12px] text-[color:var(--muted)]">{"Norms \u00b7 how this org actually works"}</span>
                            {norms.map((n) => (
                                <DocumentItem
                                    key={n.ref}
                                    refLabel={n.ref}
                                    text={profileItemText(n)}
                                    meta={[PREVALENCE_LABEL[n.prevalence] || n.prevalence, n.basis].filter(Boolean).join(" \u00b7 ")}
                                />
                            ))}
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}

function ProfileLines({ profile, status, docs }) {
    if (!profile && status === "loading") {
        return <span aria-busy="true" className={LINE_CLASS}>Loading the review profile...</span>;
    }
    if (!profile) {
        return <span className={LINE_CLASS}>The review profile could not be loaded. Findings remain readable.</span>;
    }
    return (
        <>
            <PolicyLine clauses={docs.clauses} />
            <NormsLine docs={docs} />
        </>
    );
}

// -- matrix rollup --------------------------------------------------------------

// Two blocks, not a cross-product. The axes are ordinal and per-axis; a
// document returns ONE verdict per finding, not one per axis. Crossing them
// filtered by "findings that have this axis" -- and since nearly every
// finding has both, each document printed the identical value twice, once
// under Risk and once under Sensitivity.
function MatrixRollup({ ledger, hasPolicy, hasNorms, hasIntents }) {
    const incidents = ledger.incidents;
    const rollup = ledgerRollup(incidents);
    const count = (axis) => incidents.filter((i) => i[axis]).length;

    const reading = (label, value, tone, n) => (
        <span className="min-w-0">
            <span className="block text-[12px] text-[color:var(--muted)]">{label}</span>
            <span className="block text-[12px] leading-[1.4]">
                <span style={{ color: SEVERITY_COLOR[tone] }}>{value || "none"}</span>
                {n ? <span className="text-[color:var(--muted-soft)]">{` \u00b7 ${n} of ${incidents.length}`}</span> : null}
            </span>
        </span>
    );

    const verdict = (label, value, tone, detail) => (
        <span className="min-w-0">
            <span className="block text-[12px] text-[color:var(--muted)]">{label}</span>
            <OverflowTooltip
                text={detail ? `${value} - ${detail}` : value}
                className="block min-w-0 text-[12px] leading-[1.4]"
            >
                <span style={{ color: SEVERITY_COLOR[tone] }}>{value}</span>
                {detail ? <span className="text-[color:var(--muted-soft)]"> {detail}</span> : null}
            </OverflowTooltip>
        </span>
    );

    // A refuted objection never counts -- its row is withheld unless something
    // else admitted it, and its clause must not read as contradicted.
    const violations = [...new Set(incidents
        .filter((i) => i.policy?.status === "violation" && i.policy.clause && i.policyClaim?.holds !== false)
        .map((i) => i.policy.clause))];
    const policyVerdict = () => {
        if (violations.length) {
            return verdict("Policy", `${violations.length} clause${violations.length === 1 ? "" : "s"} contradicted`, "danger", violations.join(" \u00b7 "));
        }
        return verdict("Policy", incidents.some((i) => i.policy?.status === "cleared") ? "no clause contradicted" : "no clause speaks to these", "calm", null);
    };
    const normsVerdict = () => {
        const dev = incidents.filter((i) => i.norms?.status === "deviation" && i.normsClaim?.holds !== false).length;
        const typ = incidents.filter((i) => i.norms?.status === "typical").length;
        if (dev) return verdict("Norms", `${dev} unusual here`, "calm", typ ? `${typ} routine` : null);
        if (typ) return verdict("Norms", `${typ} routine here`, "calm", null);
        return verdict("Norms", "no view", "calm", null);
    };
    const intentVerdict = () => {
        const counter = incidents.filter((i) => i.intent?.status === "counter" && i.intentClaim?.holds !== false).length;
        const adv = incidents.filter((i) => i.intent?.status === "advances").length;
        if (counter) return verdict("Intent", `${counter} works against an objective`, "warn", null);
        if (adv) return verdict("Intent", `${adv} serve an objective`, "calm", null);
        return verdict("Intent", "no view", "calm", null);
    };

    const docCount = (hasPolicy ? 1 : 0) + (hasNorms ? 1 : 0) + (hasIntents ? 1 : 0);
    // Literal class strings -- Tailwind's JIT can't see an interpolated one.
    const docCols = docCount === 3
        ? "[grid-template-columns:minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
        : docCount === 2
            ? "[grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]"
            : "[grid-template-columns:minmax(0,1fr)]";
    return (
        <div className="grid gap-[8px] min-w-0" data-ledger-rollup="">
            <div className="grid [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)] gap-x-[8px] min-w-0">
                {reading("Risk", rollup.behaviour, behaviourTone(rollup.behaviour), count("behaviour"))}
                {reading("Sensitivity", rollup.content, contentTone(rollup.content), count("content"))}
            </div>
            {docCount > 0 && (
                <div className={`grid ${docCols} gap-x-[8px] min-w-0 border-t border-[color:var(--line)] pt-[7px]`}>
                    {hasPolicy && policyVerdict()}
                    {hasNorms && normsVerdict()}
                    {hasIntents && intentVerdict()}
                </div>
            )}
            {ledger.refuted > 0 && (
                <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                    {ledger.refuted} objection{ledger.refuted === 1 ? "" : "s"} did not survive verification and {ledger.refuted === 1 ? "was" : "were"} set aside
                </span>
            )}
            {ledger.verifierError && (
                <span className="text-[10px] leading-[1.4]" style={{ color: SEVERITY_COLOR.warn }}>
                    Objection verification was unavailable for some findings. Their objections are unverified.
                </span>
            )}
        </div>
    );
}

// -- report summaries -----------------------------------------------------------

// Each report's summary, in window order: the judge may not spend it on its
// findings, so read in order the summaries are the session's chapters. One
// report reads as a paragraph; several carry the turns their window covered,
// with the newest windows shown and the earlier ones folded.
function ReportSummaries({ chapters, base = 0 }) {
    const [all, setAll] = useState(false);
    if (chapters.length === 1) {
        return <span className="min-w-0 break-words text-[12px] leading-[1.45] text-[color:var(--ink)]">{chapters[0].summary}</span>;
    }
    const hidden = all ? 0 : Math.max(0, chapters.length - SUMMARY_PREVIEW);
    const shown = chapters.slice(hidden);
    const span = (c) => {
        if (!c.placed) return "not placed";
        return c.from === c.through ? `turn ${base + c.from}` : `turns ${base + c.from}\u2013${base + c.through}`;
    };
    return (
        <span className="grid gap-[5px] min-w-0">
            {hidden > 0 && (
                <button type="button" onClick={() => setAll(true)} className={`${LINK_CLASS} justify-self-start`}>
                    {hidden} earlier window{hidden === 1 ? "" : "s"}
                </button>
            )}
            {/* One grid for every row, so the summaries share a left edge
                however wide each window's turn range prints. */}
            <span className="grid [grid-template-columns:auto_minmax(0,1fr)] gap-x-[8px] gap-y-[5px] min-w-0">
                {shown.map((c) => (
                    <span key={c.key} className="contents">
                        <span className="pt-[2px] whitespace-nowrap text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">{span(c)}</span>
                        <span className="min-w-0 break-words text-[12px] leading-[1.45] text-[color:var(--ink)]">{c.summary}</span>
                    </span>
                ))}
            </span>
        </span>
    );
}

// -- panel ----------------------------------------------------------------------

const HOW_TOOLTIP =
    "Saved results of the backend trace review. The pipeline judges each conversation window as it grows: it rates behaviour risk and content sensitivity, verifies cited quotes, and lowers ratings with unverifiable evidence. When configured, policy clauses, observed norms and inferred intents are included. Opening this tab reads the saved reports; it never starts a review.";

const mergeProfile = (base, scoped) => {
    if (!base) return scoped || null;
    if (!scoped) return base;
    return {
        ...base,
        policyText: scoped.policyText || base.policyText,
        norms: scoped.norms?.length ? scoped.norms : base.norms,
        intents: scoped.intents?.length ? scoped.intents : base.intents,
        discoverySummary: scoped.discoverySummary || base.discoverySummary,
    };
};

export default function BackendTraceReviewPanel({ trace, onJumpToTurn = null, onEvidence = null, onStatus = null }) {
    const traceId = trace?.sessionId || trace?.id || null;
    const [attempt, setAttempt] = useState(0);
    // The findings under the pointer, shared by the timeline and the ledger so
    // either can point at the other.
    const [hover, setHover] = useState(null);
    const onHover = useCallback((keys) => setHover(keys?.length ? new Set(keys) : null), []);
    useEffect(() => setHover(null), [traceId]);
    const [state, setState] = useState(() => readPanelCache(traceId) || {
        status: "loading",
        reports: readReportsCache(traceId)?.rows || [],
        profile: null,
        profiles: {},
        profileStatus: "loading",
        availability: "loading",
        error: null,
    });

    useEffect(() => {
        const controller = new AbortController();
        const cached = readPanelCache(traceId);
        const reportCache = readReportsCache(traceId);
        const displayed = cached || (reportCache
            ? { status: "done", reports: reportCache.rows || [], profile: null, profiles: {}, profileStatus: "loading", availability: "loading", error: null }
            : null);
        const displayedState = displayed
            ? { ...displayed, profileStatus: displayed.profile ? "done" : "loading" }
            : { status: "loading", reports: [], profile: null, profiles: {}, profileStatus: "loading", availability: "loading", error: null };
        setState(displayedState);
        const requestProfile = (scope) => getReviewProfile({ scope, signal: controller.signal })
            .then((value) => ({ status: "fulfilled", value }), (reason) => ({ status: "rejected", reason }));
        const tenantProfileRequest = requestProfile(null);
        const availabilityRequest = getReviewAvailability({ signal: controller.signal })
            .then((available) => ({ status: "fulfilled", value: available }), (reason) => ({ status: "rejected", reason }));
        tenantProfileRequest.then((result) => {
            if (controller.signal.aborted || result.status !== "fulfilled") return;
            setState((current) => {
                const profiles = { ...current.profiles, tenant: result.value };
                const next = { ...current, profile: result.value, profiles, profileStatus: "done" };
                if (current.status === "done") writePanelCache(traceId, next);
                return next;
            });
        });
        fetchTraceReviewReports({ sessionId: traceId, signal: controller.signal }).then(async (reportsResult) => {
            if (controller.signal.aborted) return;
            writeReportsCache(traceId, reportsResult);
            setState((current) => ({ ...current, status: "done", reports: reportsResult.rows || [], error: null }));
            const scopes = [];
            const seen = new Set(["tenant"]);
            for (const report of reportsResult.rows || []) {
                const scope = report.scope?.key ? report.scope : null;
                const key = scopeKeyOf(report);
                if (!seen.has(key) && scopes.length < PROFILE_SCOPE_LIMIT) {
                    seen.add(key);
                    scopes.push({ key, scope });
                }
            }
            const [tenantResult, availabilityResult, profileResults] = await Promise.all([
                tenantProfileRequest,
                availabilityRequest,
                runPooled(scopes.map(({ scope }) => () => requestProfile(scope)), PROFILE_FETCH_CONCURRENCY),
            ]);
            if (controller.signal.aborted) return;
            const profiles = { ...(cached?.profiles || {}) };
            if (tenantResult.status === "fulfilled") profiles.tenant = tenantResult.value;
            profileResults.forEach((result, index) => {
                if (result.status === "fulfilled") profiles[scopes[index].key] = result.value;
            });
            const profile = profiles.tenant || Object.values(profiles)[0] || cached?.profile || null;
            const next = {
                status: "done",
                reports: reportsResult.rows || [],
                profile,
                profiles,
                profileStatus: profile ? "done" : "error",
                availability: availabilityResult.status === "fulfilled"
                    ? (availabilityResult.value ? "available" : "unavailable")
                    : "unknown",
                error: null,
            };
            if (profile || next.availability === "unavailable") writePanelCache(traceId, next);
            setState(next);
        }, async (error) => {
            const tenantResult = await tenantProfileRequest;
            if (controller.signal.aborted) return;
            if (displayed) {
                setState((current) => ({
                    ...current,
                    profile: current.profile || (tenantResult.status === "fulfilled" ? tenantResult.value : null),
                    profiles: tenantResult.status === "fulfilled"
                        ? { ...current.profiles, tenant: tenantResult.value }
                        : current.profiles,
                    profileStatus: current.profile || tenantResult.status === "fulfilled" ? "done" : "error",
                }));
                return;
            }
            setState({ status: "error", reports: [], profile: null, profiles: {}, profileStatus: "error", availability: "unknown", error });
        }).catch((error) => {
            if (!controller.signal.aborted) console.error("[context] failed to prepare trace review:", error);
        });
        return () => controller.abort();
    }, [traceId, attempt]);

    const turns = useMemo(() => trace?.turns || [], [trace?.turns]);
    // Every turn number printed here is the one Lanes prints for that turn:
    // its place in the whole record, from zero. Context and Lanes index the
    // same turns (the code-mode unwrap inserts blocks within them).
    const base = recordTurnBase(trace?.record);
    const ledger = useMemo(() => buildLedger(state.reports, turns, { base }), [state.reports, turns, base]);
    // Each report resolves its references against the profile it was scoped
    // to; the lines above the ledger describe the newest report's.
    const docsByScope = useMemo(() => {
        const tenant = state.profiles?.tenant || state.profile;
        const map = new Map([["tenant", ledgerDocuments(tenant)]]);
        for (const [key, scoped] of Object.entries(state.profiles || {})) {
            if (key !== "tenant") map.set(key, ledgerDocuments(mergeProfile(tenant, scoped)));
        }
        return map;
    }, [state.profiles, state.profile]);
    // fetchTraceReviewReports hands reports back newest first.
    const primaryReport = state.reports[0] || null;
    const primaryScope = scopeKeyOf(primaryReport);
    const primaryProfile = mergeProfile(state.profiles?.tenant || state.profile, state.profiles?.[primaryScope]);
    const primaryDocs = docsByScope.get(primaryScope) || docsByScope.get("tenant");

    useEffect(() => {
        onStatus?.({ status: state.status, reports: state.reports.length, incidents: ledger.incidents.length });
    }, [state.status, state.reports.length, ledger.incidents.length, onStatus]);
    useEffect(() => {
        if (!onEvidence) return undefined;
        onEvidence(state.status === "done" ? evidenceHighlights(ledger.incidents, trace) : null);
        return () => onEvidence(null);
    }, [state.status, ledger, trace, onEvidence]);

    // Readable text per turn, for the citation tooltips (see the quote in its
    // surroundings rather than taking it on trust).
    const turnTexts = useMemo(() => turns.map((t) => turnText(t)), [turns]);

    const done = state.status === "done";
    const reviewed = ledger.window;
    const incidents = ledger.incidents;
    // A frame is a column when its document exists, or when a finding carries
    // its verdict: the pipeline only asks for a frame whose document the
    // profile had when the window was judged.
    const hasPolicy = primaryDocs.clauses.length > 0 || incidents.some((i) => i.policy);
    const hasNorms = primaryDocs.norms.length > 0 || incidents.some((i) => i.norms);
    const hasIntents = primaryDocs.intents.length > 0 || incidents.some((i) => i.intent);
    const unplaced = incidents.filter((i) => !i.turns.length).length;
    const range = reviewed ? `${base + reviewed.firstTurn - 1}\u2013${base + reviewed.lastTurn - 1}` : null;
    const profileVersion = primaryReport?.profileVersion || 0;
    const scoped = primaryReport?.scope?.key ? primaryReport.scope : null;
    const frames = { hasPolicy, hasNorms, hasIntents, count: rowFrameCount(hasPolicy, hasNorms, hasIntents) };
    const layout = ROW_LAYOUT[frames.count];
    const provenance = done && primaryReport
        ? `${state.reports.length === 1 ? "One saved report, judged" : `${state.reports.length} saved reports; the newest was judged`} against review profile version ${profileVersion}${scoped ? `, with the ${scoped.kind} ${scoped.key} layer at version ${primaryReport.scopeProfileVersion || 0}` : ""}.${primaryReport.model ? ` Model: ${primaryReport.model}.` : ""}`
        : null;
    const howLabel = (
        <span className="grid gap-[5px]">
            <span>{HOW_TOOLTIP}</span>
            {provenance && <span className="text-[color:var(--muted)]" data-review-provenance="">{provenance}</span>}
        </span>
    );

    return (
        <div aria-busy={state.status === "loading"} className="grid gap-[6px] min-w-0" data-backend-trace-review="">
            {/* The box titles itself -- the tab above says which reading this
                is, but the panel still needs a subject of its own for "how?"
                to attach to. Which reports were read, and against which
                profile, lives in that hover; the timeline states the turns. */}
            <div className="flex items-baseline gap-[8px] min-w-0">
                <span className="shrink-0 text-[12px] leading-[1.2] font-medium text-[color:var(--ink)]">Trace review</span>
                <HoverTooltip wide maxWidth={360} placement="top" label={howLabel}>
                    <span tabIndex={0} className="shrink-0 cursor-help text-[10px] leading-[1.2] text-[color:var(--muted-soft)] underline decoration-dotted underline-offset-2">how?</span>
                </HoverTooltip>
            </div>

            <ProfileLines profile={primaryProfile} status={state.profileStatus} docs={primaryDocs} />

            {state.status === "loading" && (
                <span className="inline-flex items-center gap-[6px] text-[11px] leading-[1.2] text-[color:var(--muted)]">
                    <span className="inline-block h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-[color:var(--line-strong)]" />
                    Loading the saved review...
                </span>
            )}

            {state.status === "error" && (
                <span role="alert" className="flex items-baseline gap-[8px] text-[11px] leading-[1.3] min-w-0">
                    <span className="min-w-0 break-words" style={{ color: SEVERITY_COLOR.danger }}>The saved review could not be loaded.</span>
                    <button type="button" onClick={() => setAttempt((n) => n + 1)} className={LINK_CLASS}>retry</button>
                </span>
            )}

            {done && !state.reports.length && (
                <span className="text-[11px] leading-[1.3] text-[color:var(--muted)]">
                    {state.availability === "unavailable"
                        ? "Review is not available in this deployment."
                        : "No review has been saved for this conversation yet."}
                </span>
            )}

            {done && state.reports.length > 0 && (
                <div className="grid gap-[8px] min-w-0">
                    <div className={`${CARD_CLASS} grid gap-[6px]`}>
                        {ledger.chapters.length > 0 && <ReportSummaries chapters={ledger.chapters} base={base} />}
                        <div className={ledger.chapters.length ? "border-t border-[color:var(--line-soft)] pt-[6px]" : ""}>
                            <MatrixRollup ledger={ledger} hasPolicy={hasPolicy} hasNorms={hasNorms} hasIntents={hasIntents} />
                        </div>
                    </div>
                    <div className={`${CARD_CLASS} grid gap-[4px]`}>
                        <div className="flex items-baseline gap-[8px] min-w-0">
                            <span className="text-[12px] text-[color:var(--muted)]">Turn timeline</span>
                            <span className="ml-auto shrink-0 text-[10px] leading-[1.2] text-[color:var(--muted-soft)]">
                                {reviewed ? `evaluated ${range} of ${base + reviewed.totalTurns}` : `${turns.length} turns`}
                            </span>
                        </div>
                        <EvaluationTimeline turns={turns} ledger={ledger} onJumpToTurn={onJumpToTurn} hover={hover} onHover={onHover} base={base} />
                        <span className="block min-w-0 break-words text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                            Tick height shows turn volume.
                            {incidents.length > unplaced
                                ? " Colored spikes mark cited evidence. Rows below list findings in turn order."
                                : " No cited evidence in the loaded turns."}
                            {unplaced > 0
                                ? ` ${unplaced} finding${unplaced === 1 ? " cites" : "s cite"} messages outside the loaded turns and ${unplaced === 1 ? "has" : "have"} no lane.`
                                : ""}
                            {!reviewed ? " The reviewed windows are not among the loaded turns." : ""}
                            {ledger.temporalNotes.length ? ` ${ledger.temporalNotes.join(" ")}` : ""}
                        </span>
                    </div>
                    {incidents.length === 0 ? (
                        <span className="text-[11px] leading-[1.3] text-[color:var(--muted)]">
                            {reviewed ? `Nothing to act on in turns ${range}.` : "Nothing to act on in the reviewed windows."}
                            {ledger.withheld > 0
                                ? ` The judge described ${ledger.withheld} action${ledger.withheld === 1 ? "" : "s"} here, and nothing objected to any of them.`
                                : ""}
                        </span>
                    ) : (
                        <div className="@container grid gap-[6px] min-w-0" data-backend-review-findings="">
                            {frames.count > 0 && (
                                <div className={`${layout.header} ${layout.cols} gap-x-[6px] px-[9px] text-[12px] text-[color:var(--muted)]`} data-ledger-header="">
                                    <span />
                                    {/* The legend lives on screen, not in the
                                        reader's head, and names every colour
                                        a dot can take. It wraps rather than
                                        truncates: a panel narrow enough to
                                        clip it would clip the last state. */}
                                    <span className="min-w-0 text-[11px] leading-[1.35]">
                                        {hasPolicy && (
                                            <>
                                                <span className="whitespace-nowrap"><span style={{ color: SEVERITY_COLOR.danger }}>{"\u25cf"}</span>{" breaks policy"}</span>{" \u00b7 "}
                                            </>
                                        )}
                                        <span className="whitespace-nowrap"><span style={{ color: SEVERITY_COLOR.warn }}>{"\u25cf"}</span>{" objects"}</span>{" \u00b7 "}
                                        <span className="whitespace-nowrap"><span className="text-[color:var(--green)]">{"\u25cf"}</span>{" vouches"}</span>{" \u00b7 "}
                                        <span className="whitespace-nowrap">{"\u25cb no objection"}</span>{" \u00b7 "}
                                        <span className="whitespace-nowrap">{"\u2013 no view"}</span>
                                    </span>
                                    {hasPolicy && <span className="text-center">Policy</span>}
                                    {hasNorms && <span className="text-center">Norms</span>}
                                    {hasIntents && <span className="text-center">Intent</span>}
                                </div>
                            )}
                            {incidents.map((inc) => (
                                <IncidentRow
                                    key={inc.key}
                                    incident={inc}
                                    docs={docsByScope.get(inc.scopeKey) || primaryDocs}
                                    turnTexts={turnTexts}
                                    frames={frames}
                                    linked={!!hover?.has(inc.key)}
                                    onHover={onHover}
                                    onJumpToTurn={onJumpToTurn}
                                    base={base}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
